import type { ActKc2StatusEnum } from 'gql/graphql';
import { ContractStatusEnum, DeviationEnum, WorkOrderStatusEnum } from 'gql/graphql';
import { DeviatedCell } from 'utils/tableCells';

import { WorkOrderPriceKC2CostEdit } from '../WorkOrderPriceKC2CostEdit';
import { WorkOrderPriceKC2VolumeEdit } from '../WorkOrderPriceKC2VolumeEdit';
import { WorkOrderPriceTypeEnum } from '../workorderTypes';

interface IRenderWorkOrderPriceKC2VolumeEdit {
    key: string;
    id: number;
    status: ActKc2StatusEnum;
    onChange: () => void;
    deviationKey: string;
    isReadOnlyKey: string;
    isDisabledByProjectStatus: boolean;
    row: any;
    workOrderStatus?: WorkOrderStatusEnum;
    contractStatus?: ContractStatusEnum;
}

interface IRenderWorkOrderPriceKC2CostEdit {
    key: string;
    id: number;
    status: ActKc2StatusEnum;
    onChange: () => void;
    isDisabledByProjectStatus: boolean;
    row: any;
    workOrderStatus?: WorkOrderStatusEnum;
    contractStatus?: ContractStatusEnum;
    decimalScale: number;
}

interface IWorkOrderPriceKC2Volume {
    key: string;
    deviationKey: string;
    row: any;
}

export const getRenderWorkOrderPriceKC2Volume = ({ key, deviationKey, row }: IWorkOrderPriceKC2Volume) => {
    const content = row[key] ?? '';

    return row[deviationKey] === DeviationEnum.Up ? <DeviatedCell>{content}</DeviatedCell> : content;
};

export const getRenderWorkOrderPriceKC2VolumeEdit = ({
    key,
    id,
    status,
    onChange,
    deviationKey,
    contractStatus,
    workOrderStatus,
    isReadOnlyKey,
    isDisabledByProjectStatus,
    row,
}: IRenderWorkOrderPriceKC2VolumeEdit) => {
    const { id: rowId, type } = row;

    const isStatusWorkOrderDelayed = workOrderStatus === WorkOrderStatusEnum.Delayed;
    const isStatusWorkOrderCompleted = workOrderStatus === WorkOrderStatusEnum.Completed;

    const disabled =
        isDisabledByProjectStatus ||
        isStatusWorkOrderDelayed ||
        isStatusWorkOrderCompleted ||
        [
            WorkOrderPriceTypeEnum.CONSTRAINT_COEFFICIENT,
            WorkOrderPriceTypeEnum.COEFFICIENT,
            WorkOrderPriceTypeEnum.TECH_PART_COEFFICIENT,
        ].includes(type) ||
        contractStatus !== ContractStatusEnum.InImplementation ||
        row[isReadOnlyKey] ||
        row.isExcludedPrice;

    return (
        <WorkOrderPriceKC2VolumeEdit
            deviation={row[deviationKey]}
            actKC2Id={id}
            workOrderPriceId={rowId}
            defaultValue={row[key]}
            onChange={onChange}
            actKC2Status={status}
            disabled={disabled}
        />
    );
};

export const getRenderWorkOrderPriceKC2CostEdit = ({
    key,
    id,
    status,
    onChange,
    contractStatus,
    workOrderStatus,
    isDisabledByProjectStatus,
    row,
    decimalScale,
}: IRenderWorkOrderPriceKC2CostEdit) => {
    const { id: rowId, type } = row;

    const isStatusWorkOrderDelayed = workOrderStatus === WorkOrderStatusEnum.Delayed;
    const isStatusWorkOrderCompleted = workOrderStatus === WorkOrderStatusEnum.Completed;

    const isEditableCell = row[`actKC2isEditableCost_${id}`];

    const disabled =
        (isEditableCell &&
            [
                WorkOrderPriceTypeEnum.CONSTRAINT_COEFFICIENT,
                WorkOrderPriceTypeEnum.COEFFICIENT,
                WorkOrderPriceTypeEnum.TECH_PART_COEFFICIENT,
            ].includes(type)) ||
        isDisabledByProjectStatus ||
        isStatusWorkOrderDelayed ||
        isStatusWorkOrderCompleted ||
        contractStatus !== ContractStatusEnum.InImplementation ||
        row.isExcludedPrice;

    return (
        <WorkOrderPriceKC2CostEdit
            actKC2Id={id}
            workOrderPriceId={rowId}
            defaultValue={row[key]}
            onChange={onChange}
            actKC2Status={status}
            disabled={disabled}
            decimalScale={decimalScale}
        />
    );
};
